import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District } from '@citizen-walk/shared-types';
import { useEffect, useState, type FormEvent } from 'react';
import { updateDistrict } from './useDistricts';

/**
 * 地区の「投稿可能エリア」(postAreaCenter / postAreaRadiusM) 専用編集パネル
 * (admin 第 20 弾、Phase 5.4、GAP-U3 admin 側補完 + functions 第 4 弾 reviewer ⚠️D close)。
 *
 * 経緯: user 第 12 弾 (GAP-U3) で user 側に PostForm の area check + 警告 UI を実装したが、
 * admin 側で `postAreaCenter` / `postAreaRadiusM` を編集できるのは `DistrictEditForm` (Modal、
 * 基本情報まとめて編集) だけで、運用上 area 設定だけを差し替えたい場合に誤操作リスクがあった。
 * 本パネルは A-S3 地区詳細画面に**独立した section** として常設し、save button も独立、
 * 「エリア制限を解除する (clear)」 1-click action も提供する。
 *
 * 設計判断:
 *   - 既存 `DistrictEditForm` (Modal) は touch せず、独立 panel として並置 → 既存 UX を壊さない
 *   - 「エリア制限解除」は 2 field を **明示的に null** で送信 → `updateDistrict` の `null` 削除セマンティクス
 *     (`postAreaCenter === null` → `payload.postAreaCenter = null`、Firestore で field 削除相当の null 化)
 *     を再利用。完全 deleteField sentinel への切替は別 sprint (現状の null 化で user 側の
 *     `district.postAreaCenter ?? null` 判定が正しく `skipped` になる、user 第 12 弾で確認済)
 *   - 入力は `<input type="number" step min max>` の native validation + form 送信時に範囲再 check
 *     → keyboard / paste / 直接 typing の何れでも範囲外を弾く
 *   - 認可 (firestore.rules:65-68): `allow update: if isMaster()` のため、現状 master のみが
 *     save を成功させられる。district admin 経路を rules 拡張するのは将来 sprint (本 sprint は UI 側
 *     guard も master 限定で、rules と整合)
 */

export interface DistrictPostAreaPanelProps {
  /** 対象 district (read-only、save 後は parent の onSnapshot で再描画されてここも更新される)。 */
  district: District;
  /** 操作者 uid (createdBy / updatedBy 用)。 */
  uid: string;
  /** 編集権限 (master のみ true、firestore.rules:67 と整合)。 */
  canEdit: boolean;
}

interface DraftState {
  lat: string;
  lng: string;
  radius: string;
}

/**
 * District の field 値から draft string state を作る (空欄は ''、数値は String 化)。
 * test しやすさのため pure function に切出し、`DistrictPostAreaPanel.test.ts` から検証する。
 */
export function draftFromDistrict(
  d: Pick<District, 'postAreaCenter' | 'postAreaRadiusM'>,
): DraftState {
  return {
    lat: d.postAreaCenter ? String(d.postAreaCenter.latitude) : '',
    lng: d.postAreaCenter ? String(d.postAreaCenter.longitude) : '',
    radius: typeof d.postAreaRadiusM === 'number' ? String(d.postAreaRadiusM) : '',
  };
}

export type ParseResult =
  | { kind: 'clear' }
  | { kind: 'set'; latitude: number; longitude: number; radiusM: number }
  | { kind: 'invalid'; reason: string };

/**
 * draft string → 保存ペイロード or エラー理由 を返す pure 判定。
 *
 * - 3 field すべて空: `clear` (= 制限解除、user 側 area check skip 動作)
 * - 3 field すべて埋まり、かつ範囲内: `set`
 * - それ以外 (部分入力 / 範囲外 / NaN): `invalid`
 *
 * 範囲制約 (旧 v1 admin の同等画面踏襲 + v2 で再 tighten):
 *   - 緯度: -90..90 (含む)、経度: -180..180 (含む) — Firestore GeoPoint の範囲
 *   - 半径: 50..100000 m (50m〜100km)、user 側 isWithinArea が 0 以下を false にする現状動作と互換
 */
export function parsePostAreaDraft(draft: DraftState): ParseResult {
  const lat = draft.lat.trim();
  const lng = draft.lng.trim();
  const rad = draft.radius.trim();

  if (!lat && !lng && !rad) return { kind: 'clear' };

  if (!lat || !lng || !rad) {
    return {
      kind: 'invalid',
      reason: '緯度・経度・半径は 3 つすべて入力するか、すべて空欄にしてください',
    };
  }

  const latN = Number(lat);
  const lngN = Number(lng);
  const radN = Number(rad);

  if (!Number.isFinite(latN) || !Number.isFinite(lngN) || !Number.isFinite(radN)) {
    return { kind: 'invalid', reason: '数値として解釈できない入力があります' };
  }
  if (latN < -90 || latN > 90) {
    return { kind: 'invalid', reason: '緯度は -90 〜 90 の範囲で入力してください' };
  }
  if (lngN < -180 || lngN > 180) {
    return { kind: 'invalid', reason: '経度は -180 〜 180 の範囲で入力してください' };
  }
  if (radN < 50 || radN > 100000) {
    return { kind: 'invalid', reason: '半径は 50m 〜 100000m (100km) の範囲で入力してください' };
  }

  return { kind: 'set', latitude: latN, longitude: lngN, radiusM: radN };
}

export function DistrictPostAreaPanel({ district, uid, canEdit }: DistrictPostAreaPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DraftState>(() => draftFromDistrict(district));
  const [saving, setSaving] = useState(false);

  // 親 (onSnapshot) で district が更新されたら draft を同期し直す。
  // - 編集中の値を上書きしないよう、id / postAreaCenter / postAreaRadiusM が変わった時のみ反映。
  // - 編集途中で別 admin が同時編集した場合、最新値で draft を上書きする (last-writer-wins、本 sprint OK)
  useEffect(() => {
    setDraft(draftFromDistrict(district));
  }, [district.id, district.postAreaCenter, district.postAreaRadiusM]);

  function update<K extends keyof DraftState>(key: K, value: DraftState[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function handleClear() {
    setDraft({ lat: '', lng: '', radius: '' });
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const parsed = parsePostAreaDraft(draft);
    if (parsed.kind === 'invalid') {
      toast('error', parsed.reason);
      return;
    }
    setSaving(true);
    try {
      if (parsed.kind === 'clear') {
        await updateDistrict({
          id: district.id,
          uid,
          postAreaCenter: null,
          postAreaRadiusM: null,
        });
        toast('success', '投稿可能エリアを解除しました (制限なし)');
      } else {
        await updateDistrict({
          id: district.id,
          uid,
          postAreaCenter: { latitude: parsed.latitude, longitude: parsed.longitude },
          postAreaRadiusM: parsed.radiusM,
        });
        toast('success', '投稿可能エリアを保存しました');
      }
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const currentlySet = !!district.postAreaCenter && typeof district.postAreaRadiusM === 'number';

  return (
    <Card padding="lg">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-bold text-slate-900">投稿可能エリア</h2>
          <p className="text-xs text-slate-500 mt-1">
            user 側 PostForm で現在位置がこの円内にあるときのみ投稿を許可します (3
            つすべて空欄にして保存すると制限解除、REQ-4.1.10 / GAP-U3)。
          </p>
        </div>
        <span
          className={
            currentlySet
              ? 'shrink-0 rounded px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700'
              : 'shrink-0 rounded px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-600'
          }
        >
          {currentlySet ? '設定中' : '制限なし'}
        </span>
      </div>

      {!canEdit && (
        <div className="mb-3 p-2 rounded bg-slate-50 text-[11px] text-slate-600">
          ※ この section の編集権限は master role に限ります (firestore.rules)。
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
        <div className="grid grid-cols-2 gap-2">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">中心 緯度 (-90 〜 90)</span>
            <input
              type="number"
              step="0.000001"
              min={-90}
              max={90}
              value={draft.lat}
              onChange={(e) => update('lat', e.target.value)}
              disabled={!canEdit || saving}
              placeholder="例: 35.660001"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono disabled:bg-slate-50"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">中心 経度 (-180 〜 180)</span>
            <input
              type="number"
              step="0.000001"
              min={-180}
              max={180}
              value={draft.lng}
              onChange={(e) => update('lng', e.target.value)}
              disabled={!canEdit || saving}
              placeholder="例: 139.700001"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono disabled:bg-slate-50"
            />
          </label>
        </div>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">半径 (m、50 〜 100000)</span>
          <input
            type="number"
            step={50}
            min={50}
            max={100000}
            value={draft.radius}
            onChange={(e) => update('radius', e.target.value)}
            disabled={!canEdit || saving}
            placeholder="例: 1000 (= 1km)"
            className="min-h-touch px-3 border border-slate-300 rounded text-sm font-mono disabled:bg-slate-50"
          />
        </label>

        <div className="flex justify-end gap-2 flex-wrap">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleClear}
            disabled={!canEdit || saving}
          >
            エリア制限を解除する
          </Button>
          <Button type="submit" size="sm" disabled={!canEdit || saving}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
